import { Checkbox } from '@sunwebgroup/uds-ui/checkbox'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import * as React from 'react'
import { toSnakeCase } from '@/helpers/string-helpers'
import { useIntl } from '@/intl'
import {
  FilterContainer,
  FilterItemOption,
  FilterItemOptionContent,
  FilterItemOptionControl,
  FilterItemOptionTrailing,
  FilterItemOptionWrapper,
  FilterItemsContainer,
  FilterLabel,
  FilterOptionPrice,
  FilterOptionValue,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/Filters'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { updateMultiValueFilter } from '@/scopes/checkout/filterableCars/domain/actions/multiFilter'
import { useFilter } from '@/scopes/checkout/filterableCars/domain/hooks'
import { CarFilterType } from '@/scopes/checkout/filterableCars/domain/types'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

type AdditionalServicesFilterType = 'airconditioning' | 'automaat' | '4_wheel_drive'

function filterTranslationTextKeys(key: AdditionalServicesFilterType): string | undefined {
  const mapping: Record<string, string> = {
    'airconditioning': 'checkout_steps_ancillaries_car_rental_filtering_filters_additional_services_airconditioning_text',
    'automaat': 'checkout_steps_ancillaries_car_rental_filtering_filters_additional_services_automaat_text',
    '4_wheel_drive': 'checkout_steps_ancillaries_car_rental_filtering_filters_additional_services_4_wheel_drive_text',
  }
  return mapping[key]
}

export function AdditionalServicesFilter() {
  const { filter, setFilterValue } = useFilter(CarFilterType.AdditionalServices)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()

  const handleFilterValueChange = React.useCallback(
    (checked: string | boolean, value: string) => {
      if (!value)
        return
      if (!filter)
        return
      setFilterValue(updateMultiValueFilter({ isChecked: checked, value })(filter))

      sendEvent(
        createCarEvent({
          result: checked ? 'Filter selected' : 'Filter deselected',
          type: value,
          description: 'Additional Services',
          element: 'radiobutton',
          validated: true,
        }),
      )
    },
    [sendEvent, filter, setFilterValue],
  )
  if (!filter)
    return null

  const { filterOptions, selectedFilters } = filter

  return (
    <FilterContainer>
      <FilterLabel>{intl.formatMessage({ id: `checkout_steps_ancillaries_car_rental_filtering_filters_additional_services_label` })}</FilterLabel>
      <FilterItemsContainer>
        {filterOptions.map((option) => {
          const { value, name, quantity, startingPrice } = option
          const filterTextKey = filterTranslationTextKeys(toSnakeCase(name) as AdditionalServicesFilterType) || ''

          const isSelected = selectedFilters.includes(value)
          return (
            <FilterItemOptionWrapper key={value}>
              <ListItem variant="outlined">
                <FilterItemOption>
                  <FilterItemOptionControl>
                    <Checkbox checked={isSelected} onCheckedChange={checked => handleFilterValueChange(checked, value)} />
                  </FilterItemOptionControl>
                  <FilterItemOptionContent>
                    <FilterOptionValue>
                      {filterTextKey ? intl.formatMessage({ id: filterTextKey }) : name}
                      {' '}
                      (
                      {quantity ?? 0}
                      )
                    </FilterOptionValue>
                  </FilterItemOptionContent>
                  {startingPrice !== undefined && startingPrice !== null && (quantity && quantity > 0)
                    ? (
                        <FilterItemOptionTrailing>
                          <FilterOptionPrice>
                            {intl.formatMessage({ id: `checkout_steps_ancillaries_car_rental_filtering_filters_from_starting_price_text` }, { price: intl.formatTruncatedCurrency(startingPrice) })}
                          </FilterOptionPrice>
                        </FilterItemOptionTrailing>
                      )
                    : ''}
                </FilterItemOption>
              </ListItem>
            </FilterItemOptionWrapper>
          )
        })}
      </FilterItemsContainer>
    </FilterContainer>
  )
}
